import { TreeStructureIcon } from '@phosphor-icons/react';
import type { TreeMenuItem } from '@refinedev/core';
import React from 'react';
import { PORTAL_NAME } from '@admin-panel/config/env';

export const groupTopLevel = (items: TreeMenuItem[]) => {
  return items.filter(i => i.children && i.children.length >= 0);
};

export const getIcon = (item: TreeMenuItem) => {
  const icon = (item.meta as any)?.icon as React.ReactNode | undefined;
  if (icon) return icon;
  return <TreeStructureIcon size={20} />;
};

export const isTreeItemActive = (item: TreeMenuItem, selectedKey?: string | null) => {
  if (!selectedKey) return false;
  if (selectedKey === item.key) return true;
  if (selectedKey.startsWith(`${item.key}/`)) return true;
  const itemRoutes = [item.route, (item as any).list].filter(Boolean) as string[];
  if (itemRoutes.some(r => selectedKey === r || selectedKey.startsWith(`${r}/`))) {
    return true;
  }
  if (item.children && item.children.length) {
    for (const child of item.children) {
      if (selectedKey === child.key || selectedKey.startsWith(`${child.key}/`)) {
        return true;
      }
      const childRoutes = [child.route, (child as any).list].filter(Boolean) as string[];
      if (childRoutes.some(r => selectedKey === r || selectedKey.startsWith(`${r}/`))) {
        return true;
      }
    }
  }
  return false;
};

export const getFirstNavigableRoute = (item: TreeMenuItem): string | undefined => {
  const self = (item as any).list || (item as any).route;
  if (self) return self as string;
  if (item.children && item.children.length) {
    for (const child of item.children) {
      const path = getFirstNavigableRoute(child);
      if (path) return path;
    }
  }
  return undefined;
};

const hasRole = (roles: Array<string>, role: string) => {
  if (role === '*') return true;
  return roles.includes(role);
};

const isSuperAdmin = (roles: Array<string>) =>
  roles.includes('SUPER_ADMIN') ||
  (PORTAL_NAME === 'vendor' && roles.includes('NEXUS_SUPER_ADMIN'));

const canAccessResourceList = (item: TreeMenuItem, roles: Array<string>) => {
  if (isSuperAdmin(roles)) return true;

  const meta = (item.meta ?? {}) as {
    masterRoles?: Array<string>;
    permissions?: Record<string, Array<string>>;
  };

  if (meta.masterRoles?.some(role => hasRole(roles, role))) {
    return true;
  }

  const listRoles = meta.permissions?.list;
  if (listRoles?.some(role => hasRole(roles, role))) {
    return true;
  }

  return false;
};

export const filterMenuItemsByRoles = (
  items: Array<TreeMenuItem>,
  roles: Array<string>,
): Array<TreeMenuItem> => {
  return items.flatMap(item => {
    const filteredChildren = item.children
      ? filterMenuItemsByRoles(item.children, roles)
      : [];
    const hasRoute = Boolean((item as any).list || item.route);
    const canAccessSelf = hasRoute ? canAccessResourceList(item, roles) : false;

    if (filteredChildren.length > 0 || canAccessSelf) {
      return [{ ...item, children: filteredChildren }];
    }

    return [];
  });
};
